import type {
  EpaApiTriFacility,
  EpaApiTriFacilityID,
  EpaApiTriFormREZ,
  EpaRcraManifest,
  EpaRcraManifestWasteLine,
} from '../types/epa-api.ts';
import type { Facility, SimpleSelectable } from '../types/db.ts';
import type { ISO8601DateOnly } from '../types/common.ts';

const epaOrigin = 'https://data.epa.gov/';
export type EpaApiOperator =
  | 'equals'
  | 'notEquals'
  | 'lessThan'
  | 'lessThanEqual'
  | 'greaterThan'
  | 'greaterThanEqual'
  | 'beginsWith'
  | 'endsWith'
  | 'contains'
  | 'excludes'
  | 'like'
  | 'notLike'
  | 'in'
  | 'notIn';

export interface EpaApiParameters<Type> {
  service: 'dmapservice';
  table: string;
  filters?: (
    | {
        column: keyof Type;
        operator: EpaApiOperator;
        value: string | undefined;
      }
    | {
        operator: 'join';
        table: string;
        leftColumn: keyof Type;
        rightColumn: keyof Type;
      }
  )[];
  filterMode?: 'and' | 'or';
  start?: number;
  end?: number;
  sort?: {
    column: keyof Type;
    direction: 'asc' | 'desc';
  } | null;
}

/*
GET https://data.epa.gov/dmapservice/tri.tab_v_tri_form_r_ez_pg/frs_id/equals/110000771193/1:1/
accept: application/json
*/

export async function epaFetch<Type>({
  service,
  table,
  filters = [],
  filterMode = 'and',
  start = 0,
  end = 0,
  sort = null,
}: EpaApiParameters<Type>): Promise<Type[]> {
  const href = new URL(epaOrigin);
  const pathParts: string[] = [service, table];

  const filteredFilters = filters.filter(function (filter) {
    return filter.operator === 'join' || filter.value !== undefined;
  });

  for (let filterIndex = 0; filterIndex < filteredFilters.length; filterIndex++) {
    const filter = filteredFilters[filterIndex];
    if (filter.operator === 'join') {
      pathParts.push('join', filter.table, filter.leftColumn as string, 'equals', filter.rightColumn as string);
    } else {
      const { column, operator, value } = filter;

      if (filterIndex > 0) {
        pathParts.push(filterMode);
      }

      pathParts.push(column as string, operator, value!);
    }
  }

  if (start !== 0 && end !== 0) {
    pathParts.push(`${start}:${end}`);
  }

  if (sort) {
    pathParts.push(`${sort.column as string}:${sort.direction}`);
  }

  href.pathname = pathParts.join('/');

  const response = await fetch(href);
  const json = await response.json();

  if (!Array.isArray(json)) {
    return Promise.reject(new Error(`Expected JSON array from EPA, got: ${JSON.stringify(json)}`));
  }

  return json;
}

export async function epaFetchFacilityRecordByID(
  facilityTriID: EpaApiTriFacilityID,
): Promise<EpaApiTriFacility | null> {
  const records = await epaFetch<EpaApiTriFacility>({
    service: 'dmapservice',
    table: 'tri.tri_facility',
    filters: [
      {
        column: 'tri_facility_id',
        operator: 'equals',
        value: facilityTriID,
      },
    ],
    start: 1,
    end: 1,
  });

  return records[0] ?? null;
}

export function epaFetchFormREz<Cas extends string>(
  triFacilityID: EpaApiTriFacilityID,
  year: number,
): Promise<EpaApiTriFormREZ<Cas>[]> {
  return epaFetch<EpaApiTriFormREZ<Cas>>({
    service: 'dmapservice',
    table: 'tri.tri_form_r_ez',
    filters: [
      {
        column: 'tri_facility_id',
        operator: 'equals',
        value: triFacilityID,
      },
      {
        column: 'reporting_year',
        operator: 'equals',
        value: String(year),
      },
    ],
  });
}

async function epaFetchDunBradstreetCode(triFacilityID: EpaApiTriFacilityID): Promise<string[]> {
  const result = await epaFetch<{
    asgn_db_ind: string;
    db_num: string;
    tri_facility_id: string;
  }>({
    service: 'dmapservice',
    table: 'tri.tri_facility_db',
    filters: [
      {
        column: 'tri_facility_id',
        operator: 'equals',
        value: triFacilityID,
      },
    ],
  });

  return result.map((r) => r.db_num).filter((val) => val !== 'NA');
}

async function epaFetchNpdesPermitNumbers(triFacilityID: EpaApiTriFacilityID): Promise<string[]> {
  const result = await epaFetch<{
    asgn_db_ind: string;
    npdes_num: string;
    tri_facility_id: string;
  }>({
    service: 'dmapservice',
    table: 'tri.tri_facility_npdes',
    filters: [
      {
        column: 'tri_facility_id',
        operator: 'equals',
        value: triFacilityID,
      },
    ],
  });

  return result.map((r) => r.npdes_num).filter((val) => val !== 'NA');
}

async function epaFetchRcraIdNumbers(triFacilityID: EpaApiTriFacilityID): Promise<string[]> {
  const result = await epaFetch<{
    asgn_db_ind: string;
    rcra_num: string;
    tri_facility_id: string;
  }>({
    service: 'dmapservice',
    table: 'tri.tri_facility_rcra',
    filters: [
      {
        column: 'tri_facility_id',
        operator: 'equals',
        value: triFacilityID,
      },
    ],
  });

  return result.map((r) => r.rcra_num).filter((val) => val !== 'NA');
}

async function epaFetchUicIdNumbers(triFacilityID: EpaApiTriFacilityID): Promise<string[]> {
  const result = await epaFetch<{
    asgn_db_ind: string;
    uic_num: string;
    tri_facility_id: string;
  }>({
    service: 'dmapservice',
    table: 'tri.tri_facility_uic',
    filters: [
      {
        column: 'tri_facility_id',
        operator: 'equals',
        value: triFacilityID,
      },
    ],
  });

  return result.map((r) => r.uic_num).filter((val) => val !== 'NA');
}

function coalesceNa(val: string | null): string {
  if (val === null || val === 'NA') {
    return '';
  }

  return val;
}

export async function epaFacilityToAppFacility(
  facilityTriID: EpaApiTriFacilityID,
): Promise<null | Omit<
  SimpleSelectable<Facility>,
  | 'tenantID'
  | 'facilityID'
  | 'facilityGroupID'
  | 'triID'
  | 'name'
  | 'createdBy'
  | 'createdAt'
  | 'updatedAt'
  | 'updatedBy'
  | 'deletedAt'
  | 'deletedBy'
  | 'timeZone'
>> {
  const [facility, formREZ, dunBradstreetCodes, npdesPermitNumbers, rcraIdNumbers, uicIdNumbers] = await Promise.all([
    epaFetchFacilityRecordByID(facilityTriID),
    epaFetchFormREz(facilityTriID, new Date().getUTCFullYear() - 2),
    epaFetchDunBradstreetCode(facilityTriID),
    epaFetchNpdesPermitNumbers(facilityTriID),
    epaFetchRcraIdNumbers(facilityTriID),
    epaFetchUicIdNumbers(facilityTriID),
  ]);
  if (!facility) {
    return null;
  }

  const naicsCodes: string[] = [];
  let certifierName = '';
  let certifierTitle = '';
  for (const { primary_naics_code, certif_name, certif_official_title } of formREZ) {
    if (typeof primary_naics_code === 'string' && primary_naics_code && !naicsCodes.includes(primary_naics_code)) {
      naicsCodes.push(primary_naics_code);
    }

    if (typeof certif_name === 'string' && certif_name) {
      certifierName = certif_name;
    }

    if (typeof certif_official_title === 'string' && certif_official_title) {
      certifierTitle = certif_official_title;
    }
  }

  return {
    properName: facility.facility_name ?? '',
    dunBradstreetCodes,
    npdesPermitNumbers,
    rcraIdNumbers,
    uicIdNumbers,
    naicsCodes,
    parentCompanyName: coalesceNa(facility.parent_co_name),
    parentCompanyDunBradstreetCode: coalesceNa(facility.parent_co_db_num),
    foreignParentCompanyName: coalesceNa(facility.foreign_parent_co_name),
    foreignParentCompanyDunBradstreetCode: coalesceNa(facility.foreign_parent_co_db_num),
    address: facility.street_address ?? '',
    city: facility.city_name ?? '',
    county: facility.county_name ?? '',
    state: facility.state_abbr ?? '',
    zip: facility.zip_code ?? '',
    mailingAddress: facility.mail_street_address ?? '',
    mailingCity: facility.mail_city ?? '',
    mailingState: facility.mail_state_abbr ?? '',
    mailingZip: facility.mail_zip_code ?? '',
    certifierName,
    certifierTitle,
    publicContactName: facility.asgn_public_contact ?? '',
    publicContactEmail: facility.asgn_public_contact_email ?? '',
    publicContactPhone: facility.asgn_public_phone ?? '',
    publicContactPhoneExt: facility.asgn_public_phone_ext ?? '',
    technicalContactName: facility.asgn_technical_contact ?? '',
    technicalContactEmail: facility.asgn_technical_contact_email ?? '',
    technicalContactPhone: facility.asgn_technical_phone ?? '',
    technicalContactPhoneExt: facility.asgn_technical_phone_ext ?? '',
    timeZoneConfigured: null,
  };
}

export function epaIsTrueFlag(val: string | number | null): boolean {
  return val === '1' || val === 1;
}

// While in theory the TRI EZ data should have the RCRA info, it seems like it is not always present, but we can query
// into the FRS data and find it that way:
export async function epaRcraCodesFromTriID(triID: EpaApiTriFacilityID): Promise<string[]> {
  const rows = await epaFetch<{
    tri_facility_id: string;
    pgm_sys_acrnm: string;
    pgm_sys_id: string;
    epa_registry_id: string;
    registry_id: string;
  }>({
    service: 'dmapservice',
    table: 'tri.tri_facility',
    filters: [
      {
        column: 'tri_facility_id',
        operator: 'equals',
        value: triID,
      },
      {
        operator: 'join',
        table: 'frs.frs_program_facility',
        leftColumn: 'epa_registry_id',
        rightColumn: 'registry_id',
      },
      {
        column: 'pgm_sys_acrnm',
        operator: 'equals',
        value: 'RCRAINFO',
      },
    ],
  });

  return rows.map((r) => r.pgm_sys_id);
}

export async function epaRcraManifests(
  rcraID: string,
  minDate: ISO8601DateOnly,
  maxDate: ISO8601DateOnly,
): Promise<(EpaRcraManifest & EpaRcraManifestWasteLine)[]> {
  return await epaFetch<EpaRcraManifest & EpaRcraManifestWasteLine>({
    service: 'dmapservice',
    table: 'rcra.rcr_em_manifest',
    filters: [
      {
        column: 'generator_id',
        operator: 'equals',
        value: rcraID,
      },
      {
        column: 'shipped_date',
        operator: 'greaterThanEqual',
        value: minDate.replace(/-/g, ''),
      },
      {
        column: 'shipped_date',
        operator: 'lessThanEqual',
        value: maxDate.replace(/-/g, ''),
      },
      {
        operator: 'join',
        table: 'rcra.rcr_em_waste_line',
        leftColumn: 'manifest_tracking_number',
        rightColumn: 'manifest_tracking_number',
      },
    ],
  });
}
